<script setup>
/* eslint-disable no-console */
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { useAlert } from 'dashboard/composables';
import { resolveOT, updateCaseToCancelled } from 'dashboard/api/cea';
import ServiceOrdersAPI from 'dashboard/api/serviceOrders';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  order: {
    type: Object,
    required: true,
  },
  aquasisData: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['close', 'cancelled']);

const { t } = useI18n();

const dialogRef = ref(null);
const isSubmitting = ref(false);
const nonExecutionMotive = ref('');
const solutionDescription = ref('');

const isAquasisLinked = computed(
  () =>
    !!(props.order?.aquasis_order_id && String(props.order.aquasis_order_id).trim())
);

const isFormValid = ref(false);
const updateFormValid = () => {
  isFormValid.value = isAquasisLinked.value
    ? nonExecutionMotive.value.trim().length > 0
    : true;
};

const resetForm = () => {
  nonExecutionMotive.value = '';
  solutionDescription.value = '';
  isFormValid.value = false;
};

const handleClose = () => {
  resetForm();
  emit('close');
};

const handleSubmit = async () => {
  if (!isFormValid.value || isSubmitting.value) return;

  try {
    isSubmitting.value = true;

    if (!isAquasisLinked.value) {
      await ServiceOrdersAPI.update(props.order.id, { status: 'cancelled' });
      useAlert(t('ORDERS.AQUASIS.CANCEL_LOCAL_SUCCESS'));
      emit('cancelled');
      handleClose();
      return;
    }

    const now = new Date().toISOString();
    const payload = {
      operationalSiteID: props.aquasisData?.otData?.operationalSiteID || '',
      installationID: props.aquasisData?.otData?.installationID || '',
      systemOrigin: '',
      otClass: '0',
      otOrigin: props.order.aquasis_order_id,
      endDateOt: now,
      endLastTaskOt: now,
      finalSolution: '',
      solutionDescription: solutionDescription.value,
      nonExecutionMotive: nonExecutionMotive.value,
      executorIdentifier: '',
      executorName: '',
      companyExecutorIdentifier: '',
      companyExecutorName: '',
      transmitterInstalled: '0',
      language: 'es',
      suspensionLevel: '0',
      longitude: '',
      latitude: '',
      coordinatesType: '',
      codificationType: '',
      captureDate: '',
      resolutionElements: [],
    };

    const xmlDoc = await resolveOT(payload);

    const returnEl =
      xmlDoc.getElementsByTagName('return')[0] ||
      xmlDoc.getElementsByTagName('resolveOTReturn')[0] ||
      xmlDoc.getElementsByTagName('resolveOTResponse')[0];

    const resultText = returnEl?.textContent?.trim()?.toLowerCase();
    const success = resultText === 'true' || resultText === '1';

    if (!success) {
      useAlert('Aquasis no pudo cancelar la orden. Verifique los datos.');
      return;
    }

    // Cancel CEA case (best-effort, non-blocking)
    const caseId =
      props.order.ticket?.custom_attributes?.caso_sn ||
      props.order.ticket?.custom_attributes?.sys_id ||
      props.order.ticket?.metadata?.caso_sn ||
      props.order.ticket?.metadata?.sys_id;
    if (caseId) {
      updateCaseToCancelled(caseId).catch(err =>
        console.error('Failed to cancel CEA case:', err)
      );
    }

    // Update local status to cancelled
    await ServiceOrdersAPI.update(props.order.id, { status: 'cancelled' });

    useAlert('Orden cancelada exitosamente');
    emit('cancelled');
    handleClose();
  } catch (error) {
    console.error('Error cancelling order:', error);
    useAlert('Error al cancelar la orden');
  } finally {
    isSubmitting.value = false;
  }
};

watch(
  () => props.show,
  newVal => {
    if (newVal) {
      resetForm();
      updateFormValid();
      dialogRef.value?.open();
    } else {
      dialogRef.value?.close();
    }
  }
);

watch(isAquasisLinked, () => {
  updateFormValid();
});

const submitLabel = computed(() =>
  isSubmitting.value
    ? t('ORDERS.AQUASIS.CANCEL_PENDING')
    : t('ORDERS.AQUASIS.CANCEL_CONFIRM')
);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <Dialog
    ref="dialogRef"
    width="md"
    :title="t('ORDERS.AQUASIS.CANCEL_TITLE')"
    overflow-y-auto
    :is-loading="isSubmitting"
    :confirm-button-label="submitLabel"
    :disable-confirm-button="!isFormValid || isSubmitting"
    @close="handleClose"
    @confirm="handleSubmit"
  >
    <div class="flex flex-col gap-4">
      <div
        v-if="isAquasisLinked"
        class="bg-n-solid-3 rounded-lg p-3 border border-n-weak"
      >
        <div class="flex items-center justify-between">
          <span class="text-sm font-medium text-n-slate-11">{{
            $t('ORDERS.AQUASIS.CANCEL_LINKED_REF')
          }}</span>
          <span class="font-mono text-sm font-semibold text-n-brand">
            {{ order.aquasis_order_id }}
          </span>
        </div>
      </div>
      <p v-else class="text-sm text-n-slate-12">
        {{ $t('ORDERS.AQUASIS.CANCEL_LOCAL_DESC') }}
      </p>

      <template v-if="isAquasisLinked">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('ORDERS.AQUASIS.CANCEL_MOTIVE_LABEL') }}
            <span class="text-n-ruby-11">*</span>
          </label>
          <input
            v-model="nonExecutionMotive"
            type="text"
            :placeholder="$t('ORDERS.AQUASIS.CANCEL_MOTIVE_PLACEHOLDER')"
            class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-brand"
            @input="updateFormValid"
          />
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('ORDERS.AQUASIS.CANCEL_SOLUTION_LABEL') }}
          </label>
          <textarea
            v-model="solutionDescription"
            rows="3"
            :placeholder="$t('ORDERS.AQUASIS.CANCEL_SOLUTION_PLACEHOLDER')"
            class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-brand resize-none"
          />
        </div>

        <div class="text-xs text-n-slate-10">
          <span class="text-n-ruby-11">*</span>
          {{ $t('ORDERS.AQUASIS.CANCEL_REQUIRED_FOOTER') }}
        </div>
      </template>
    </div>
  </Dialog>
</template>
